'use client';

import { useRef, useState } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { montserratMedium, montserratSemi } from "../fonts";
import HomeSectionTitle from './home-section-title';
import { FeatureCard } from './tech-section';

interface TechSectionMobileProps {
  featureCards: FeatureCard[];
}

export default function TechSectionMobile({ featureCards }: TechSectionMobileProps) {
  const t = useTranslations('home.tech');
  const techSectionRef = useRef<HTMLElement>(null);
  const [activeTechCard, setActiveTechCard] = useState(0);
  const [techTouchStart, setTechTouchStart] = useState<number | null>(null);
  const [techTouchEnd, setTechTouchEnd] = useState<number | null>(null);

  const minSwipeDistance = 50;

  const onTechTouchStart = (e: React.TouchEvent) => {
    setTechTouchEnd(null);
    setTechTouchStart(e.targetTouches[0].clientX);
  };

  const onTechTouchMove = (e: React.TouchEvent) => {
    setTechTouchEnd(e.targetTouches[0].clientX);
  };

  const onTechTouchEnd = () => {
    if (!techTouchStart || !techTouchEnd) return;
    
    const distance = techTouchStart - techTouchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && activeTechCard < featureCards.length - 1) {
      setActiveTechCard(activeTechCard + 1);
    }
    if (isRightSwipe && activeTechCard > 0) {
      setActiveTechCard(activeTechCard - 1);
    }
  };

  return (
    <section
      ref={techSectionRef}
      id="tech-section"
      className="section-container-full-height"
    >
      <HomeSectionTitle
        subtitle={t('subtitle')}
        title={t('title')}
        description={t('description')}
        centered
      />

      <div className="absolute top-[-12%] left-[-50vw] sm:left-[-50vw] z-10 pointer-events-none">
        <Image
          src="/assets/radio-3.svg"
          alt={t('altText')}
          width={402}
          loading="lazy"
          height={431} 
        />
      </div>
      
      {/* Mobile: Card Slider */}
      <div 
        className="w-full overflow-hidden relative"
        onTouchStart={onTechTouchStart}
        onTouchMove={onTechTouchMove}
        onTouchEnd={onTechTouchEnd}
      >
        {/* Dots Indicator - Vertical on Left */}
        <div className="absolute -left-0 top-1/2 -translate-y-1/2 z-20 flex flex-col gap-2">
          {featureCards.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                activeTechCard === index 
                  ? 'bg-[color:var(--main-color)] h-8' 
                  : 'bg-[color:var(--text-muted)]'
              }`}
            />
          ))}
        </div>

        <div 
          className="flex transition-transform duration-500 ease-out h-[460px]"
          style={{
            // Center based on the clamped card width (min of 70vw, 300px)
            transform: `translateX(calc(((70vw - min(70vw, 300px)) / 2) - (${activeTechCard} * (min(70vw, 300px)))))`
          }}
        >
          {featureCards.map((card, index) => (
            <div key={index} className="w-[70vw] max-w-[300px] flex-shrink-0 px-4">
              <div className="card card--border-glow group flex flex-col gap-4 justify-center items-center rounded-3xl border border-[color:var(--field-border)] bg-transparent p-0 transition-all duration-300 h-[400px] sm:min-h-[460px]">
                <div className="text-4xl flex justify-end relative pb-0 sm:pb-4">
                  <Image
                    src={(card.icon as string) || ""}
                    alt={(card.title as string) || ""}
                    width={640}
                    height={671}
                    className="opacity-100 w-[168px] h-auto sm:w-[208px] object-contain"
                  />
                </div>
                <h3
                  className={`${montserratSemi.className} text-xl sm:text-2xl px-6 text-[color:#f9f9f9]`}
                >
                  <span
                    dangerouslySetInnerHTML={{ __html: String(card.title || "").replace(/\n/g, "<br/>") }}
                  />
                </h3>
                <p
                  className={`${montserratMedium.className} text-xs sm:text-sm text-[color:#d2d2d2] px-6 pb-8 flex-grow`}
                >
                  {card.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

